import React, { useState, useEffect } from 'react';
import { integrationsAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useConversations } from '../contexts/ConversationsContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ConfirmDialog from './ConfirmDialog';

// Claude Code-style collapsible sidebar, closed by default.
// A single floating toggle button opens AND closes it: when the sidebar is
// open the button slides along to its top-right corner, so the same click
// position toggles both ways. No rail — content gets the full width.
const Sidebar = () => {
  const { user, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const {
    conversations,
    currentId,
    newChat,
    openConversation,
    renameConversation,
    deleteConversation
  } = useConversations();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // closed by default
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const isAdmin = user?.role === 'admin';
  const onChatPage = location.pathname === '/chat';

  // Whether this account's plan includes connectors / MCP. The link is hidden
  // when neither is available, so a Basic customer isn't shown a page that
  // would only tell them no. The server enforces it regardless — see
  // routes/myIntegrations.js.
  const [features, setFeatures] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    integrationsAPI.features()
      .then(f => { if (!cancelled) setFeatures(f); })
      // No flags means no link. Failing closed here is only a cosmetic loss —
      // the user can still reach /integrations directly if they know it.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const hasIntegrations = !!(features?.connectors || features?.mcp);

  const handleNewChat = () => {
    newChat();
    if (!onChatPage) navigate('/chat');
    setOpen(false);
  };

  const handleOpenConversation = (id) => {
    openConversation(id);
    if (!onChatPage) navigate('/chat');
    setOpen(false);
  };

  const handleRename = (e, id, currentTitle) => {
    e.stopPropagation();
    const title = window.prompt('Rename conversation:', currentTitle);
    if (title && title.trim()) renameConversation(id, title.trim());
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    deleteConversation(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  const navItems = [
    {
      to: '/chat',
      label: 'Chat',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )
    },
    ...(hasIntegrations ? [{
      to: '/integrations',
      label: 'Integrations',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m8.156-4.328l1.5-1.5a4 4 0 115.656 5.656l-3 3a4 4 0 01-5.656 0" />
        </svg>
      )
    }] : []),
    ...(isAdmin ? [{
      to: '/admin/users',
      label: 'User Management',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    }] : [])
  ];

  return (
    <>
      {/* Single floating toggle: opens when closed, closes when open.
          Slides to the sidebar's top-right corner while open. */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed top-3 z-[60] p-2 rounded-lg shadow-lg border transition-all duration-200
          bg-white/90 border-gray-300 text-gray-600 hover:text-gray-900
          dark:bg-gray-800 dark:border-gray-700/50 dark:text-gray-300 dark:hover:text-white
          ${open ? 'left-[212px]' : 'left-3 backdrop-blur'}`}
        title={open ? 'Close sidebar' : 'Open sidebar'}
      >
        {open ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {/* Mobile: dark overlay behind the open sidebar */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar — width animates on desktop (pushes content), slides on mobile */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 flex flex-col
          bg-white dark:bg-gray-900 overflow-hidden
          transition-all duration-200 w-64
          ${open
            ? 'translate-x-0 md:w-64 border-r border-gray-200 dark:border-gray-700/40'
            : '-translate-x-full md:translate-x-0 md:w-0 border-r-0'}`}
      >
        {/* Fixed-width inner wrapper so content doesn't squish while animating */}
        <div className="w-64 h-full flex flex-col">
          {/* Brand */}
          <div className="px-4 py-5 border-b border-gray-200 dark:border-gray-700/40">
            <Link
              to="/chat"
              onClick={() => setOpen(false)}
              className="block text-lg font-bold text-gray-900 dark:text-white hover:text-blue-500 dark:hover:text-blue-400 transition-colors leading-tight"
            >
              ROUT TECHNICAL AI
            </Link>
            <p className="text-gray-500 text-xs mt-1">Intelligent Chat</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.to
                    ? 'bg-blue-500/10 text-blue-600 border border-blue-500/30 dark:bg-blue-600/20 dark:text-blue-400 dark:border-blue-600/30'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800/70 border border-transparent'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}

            {/* Chat history */}
            <div className="pt-3">
              <button
                onClick={handleNewChat}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Chat
              </button>

              <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Recent
              </p>

              {conversations.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">No saved chats yet.</p>
              ) : (
                <div className="space-y-0.5">
                  {conversations.map((c) => (
                    <div
                      key={c._id}
                      onClick={() => handleOpenConversation(c._id)}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                        currentId === c._id
                          ? 'bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-white'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800/70'
                      }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span className="flex-1 truncate">{c.title || 'New Chat'}</span>
                      <button
                        onClick={(e) => handleRename(e, c._id, c.title)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-500 transition-opacity"
                        title="Rename"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, c._id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                        title="Delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Bottom: theme toggle + user profile + logout */}
          <div className="px-3 py-4 border-t border-gray-200 dark:border-gray-700/40">
            {/* Dark / light mode toggle */}
            <button
              onClick={toggleTheme}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 mb-3 rounded-lg text-sm font-medium
                text-gray-600 hover:text-gray-900 hover:bg-gray-100
                dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/70 transition-colors"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span className="flex items-center gap-2">
                {isDark ? (
                  // Moon icon (currently dark)
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : (
                  // Sun icon (currently light)
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
                {isDark ? 'Dark mode' : 'Light mode'}
              </span>
              {/* Little pill switch indicator */}
              <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isDark ? 'bg-blue-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDark ? 'translate-x-4' : 'translate-x-1'}`} />
              </span>
            </button>

            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-9 h-9 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-semibold">
                  {user?.name?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-gray-900 dark:text-white text-sm font-medium truncate">{user?.name}</p>
                  {isAdmin && (
                    <span className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                      Admin
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-xs truncate">{user?.email}</p>
              </div>
            </div>

            <button
              onClick={signOut}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600/10 hover:bg-red-600/20 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 rounded-lg border border-red-600/30 transition-all duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </aside>

      <ConfirmDialog
        open={!!confirmDeleteId}
        title="Delete conversation?"
        message="This chat and its messages will be permanently removed. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </>
  );
};

export default Sidebar;
